import { useEffect, useRef } from 'react';

import { useUnsavedChangesStore } from '@/shared/store/unsavedChanges.store';

type UseUnsavedChangesGuardParams = {
  enabled?: boolean;
  hasUnsavedChanges: () => boolean;
  onCancel?: () => void;
  onConfirm?: () => Promise<void>;
  onTrigger?: () => Promise<boolean>;
  formId: string;
};

export const useUnsavedChangesGuard = ({
  hasUnsavedChanges,
  onCancel,
  onConfirm,
  onTrigger,
  enabled = true,
  formId,
}: UseUnsavedChangesGuardParams) => {
  const registerForm = useUnsavedChangesStore((state) => state.registerForm);
  const unregisterForm = useUnsavedChangesStore((state) => state.unregisterForm);
  const callbacksRef = useRef({
    hasUnsavedChanges,
    onCancel,
    onConfirm,
    onTrigger,
  });

  callbacksRef.current = { hasUnsavedChanges, onCancel, onConfirm, onTrigger };

  useEffect(() => {
    if (!enabled) {
      unregisterForm(formId);
      return undefined;
    }

    registerForm(formId, {
      hasUnsavedChanges: () => callbacksRef.current.hasUnsavedChanges(),
      onCancel: () => callbacksRef.current.onCancel?.(),
      onConfirm: async () => {
        await callbacksRef.current.onConfirm?.();
      },
      onTrigger: async () => {
        return (await callbacksRef.current.onTrigger?.()) ?? true;
      },
    });

    return () => {
      unregisterForm(formId);
    };
  }, [enabled, registerForm, unregisterForm, formId]);

  return formId;
};
